"use client";
import { ReactElement, useState, useEffect, useCallback, useRef } from "react";
import dynamic from "next/dynamic";
import { toast } from "sonner";
import { cn } from "@/lib/cn";
import { getSlots, bookSlot, isErrorResponse } from "@/lib/api";
import { getUserTimezone, safeZone, todayInZone } from "@/lib/timezones";
import { normalizeWebsite } from "@/lib/website";
import { BookingCalendar } from "@/components/BookingCalendar";
import { TimeSlotList } from "@/components/TimeSlotList";
import { DurationPills } from "@/components/DurationPills";
import { TimezoneSelector } from "@/components/TimezoneSelector";
import { RecordingConsentCheckbox } from "@/components/RecordingConsentCheckbox";
import { BookingConfirmation } from "@/components/BookingConfirmation";
import { MeetingAgenda } from "@/components/MeetingAgenda";
import { ErrorState } from "@/components/ErrorState";
import { KrisBadge } from "@/components/KrisBadge";
import type {
  SlotsResponse,
  SlotEntry,
  BookResponse,
  LeadDetails,
  LeadDetailsErrors,
} from "@/lib/types";

// The phone input drags in libphonenumber's metadata, and this form only
// renders once a slot has been picked. Leads who bounce at slot selection —
// the majority, on a public page — should not pay for it on first paint.
const LeadDetailsForm = dynamic(
  () =>
    import("@/components/LeadDetailsForm").then((m) => ({
      default: m.LeadDetailsForm,
    })),
  {
    ssr: false,
    loading: () => (
      <div className="space-y-5" aria-hidden>
        {[0, 1, 2, 3].map((i) => (
          <div key={i}>
            <div className="mb-1.5 h-4 w-24 animate-pulse rounded bg-neutral-100" />
            <div className="h-11 w-full animate-pulse rounded-lg bg-neutral-100" />
          </div>
        ))}
      </div>
    ),
  }
);

type Phase = "loading" | "select" | "details" | "booking" | "booked" | "error";

/** The failure currently taking over the page, kept with the backend's own words. */
type FatalError = { code: string; message: string };

/**
 * How stale the grid must be before returning to the tab reloads it.
 *
 * Two minutes: long enough that tabbing away to copy an email address does not
 * reload the grid under the cursor, short enough to catch anything that counts
 * as having come back later.
 */
const STALE_AFTER_MS = 2 * 60 * 1000;

/**
 * Failures that are about this ATTEMPT, not about the link — so the lead can
 * simply try again, and must keep everything they typed while they do.
 *
 * Each of these used to raise a full-page `ErrorState`, which unmounts the form
 * and discards the lead's name, email, phone, website and notes.
 * `BOOKING_IN_PROGRESS` is the sharpest: the per-AE lock is held by somebody
 * else's booking, it clears in seconds, the backend's own message says to try
 * again in a moment — and the lead had to retype the lot. `INVALID_REQUEST` is
 * the same shape from the other end: it means one of their fields needs
 * correcting, which is impossible on a page that just threw the fields away.
 *
 * Link-level codes (`LINK_*`, `NOT_BOOKED`, `CALENDAR_UNAVAILABLE`) stay fatal:
 * there is genuinely nothing to come back to, and a toast over a dead form
 * would be the misleading option.
 */
const TRANSIENT_BOOKING_ERRORS = new Set([
  "BOOKING_IN_PROGRESS",
  "RATE_LIMITED",
  "BOOKING_FAILED",
  "INVALID_REQUEST",
]);

type Props = {
  orgId: string;
  token: string;
};

type FetchSlotsOptions = {
  /**
   * Whether a failure may take over the page. The corrective fetch after the
   * initial load is optional — the lead is already looking at a working grid —
   * so its failure must not replace that grid with a full-page error.
   */
  fatal?: boolean;
};

function AvatarOrInitials({
  name,
  avatarUrl,
  size = "md",
}: {
  name: string;
  avatarUrl: string | null;
  size?: "md" | "lg";
}): ReactElement {
  const initials = name
    ? name
        .split(" ")
        .slice(0, 2)
        .map((w) => w[0])
        .join("")
        .toUpperCase()
    : "";

  const sizeClass = size === "lg" ? "h-14 w-14 text-base" : "h-10 w-10 text-sm";

  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={name}
        className={cn(
          "flex-shrink-0 rounded-full object-cover ring-2 ring-white",
          sizeClass
        )}
      />
    );
  }

  return (
    <div
      className={cn(
        "flex flex-shrink-0 items-center justify-center rounded-full bg-[#029391] font-bold text-white ring-2 ring-white",
        sizeClass
      )}
    >
      {initials}
    </div>
  );
}

export function BookingClient({ orgId, token }: Props): ReactElement {
  const [phase, setPhase] = useState<Phase>("loading");
  // Code and message travel together in one piece of state: they are read
  // together by ErrorState, and two separate setters are two chances to update
  // one and leave the other describing the previous failure.
  const [fatalError, setFatalError] = useState<FatalError>({
    code: "UNEXPECTED_ERROR",
    message: "",
  });
  const [slotsData, setSlotsData] = useState<SlotsResponse | null>(null);
  const [slotsLoading, setSlotsLoading] = useState(false);

  const [timezone, setTimezone] = useState<string>(getUserTimezone());
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [selectedDuration, setSelectedDuration] = useState<number>(30);
  const [selectedSlot, setSelectedSlot] = useState<SlotEntry | null>(null);
  const [recordingConsent, setRecordingConsent] = useState(false);
  /** Guards the window between the Confirm click and the button going disabled. */
  const submittingRef = useRef(false);
  const [leadDetails, setLeadDetails] = useState<LeadDetails>({
    name: "",
    email: "",
    phone: "",
    website: "",
    notes: "",
  });
  const [leadErrors, setLeadErrors] = useState<LeadDetailsErrors>({});
  const [bookResponse, setBookResponse] = useState<BookResponse | null>(null);

  /**
   * Monotonic id of the newest /slots request. Responses can land out of order
   * — a slow date followed by a fast one — and the corrective fetch below is
   * issued while the calendar, duration pills and picker are already
   * interactive, so a lead who clicks a date inside that window would otherwise
   * get another day's slots dropped under the heading they are reading. Only
   * the newest request may write state.
   */
  const requestIdRef = useRef(0);

  /**
   * When the grid on screen was last answered for.
   *
   * A booking page gets left open. The lead opens it at noon, is offered times
   * from 6pm because the link asks six hours' notice, wanders off, and comes
   * back at five. Nothing has moved on screen, but every slot showing is now
   * inside the notice window and the backend will refuse all of them —
   * `checkSlotIsOffered` re-derives the same grid at write time, so the AE is
   * never short-changed. The lead is: they fill in name, email and phone, and
   * only then hear the slot is gone, in a sentence that says someone took it
   * when nothing of the sort happened.
   */
  const lastLoadedAtRef = useRef(0);

  const fetchSlots = useCallback(
    async (
      date: string,
      duration: number,
      tz: string,
      { fatal = true }: FetchSlotsOptions = {}
    ): Promise<void> => {
      const requestId = ++requestIdRef.current;
      // Stamped here, not when the answer lands, and that ordering is the whole
      // point. Two handlers feed this — `visibilitychange` and `focus` both fire
      // on one return to the tab — so stamping on success let the second one
      // read the old value and issue a duplicate request. Stamping on failure
      // too is deliberate: otherwise one RATE_LIMITED blip leaves the page
      // permanently "stale", re-requesting and re-toasting on every later focus,
      // which is exactly how a rate limit stays tripped.
      lastLoadedAtRef.current = Date.now();
      setSlotsLoading(true);
      setSelectedSlot(null);
      const res = await getSlots(orgId, token, tz, date, duration);
      // A newer request owns the UI now, so this answer is stale. `slotsLoading`
      // is deliberately left alone: the request that superseded this one is
      // still in flight and will clear the skeleton when it lands.
      if (requestId !== requestIdRef.current) return;
      setSlotsLoading(false);

      if (isErrorResponse(res)) {
        if (res.error === "SLOT_UNAVAILABLE" || res.error === "SLOT_EXPIRED") {
          toast.error(
            res.error === "SLOT_UNAVAILABLE"
              ? "This slot was just taken — please pick another time."
              : "This slot is in the past — please pick another time."
          );
          return;
        }
        // Browsing dates is not booking. A rate limit or a transient backend
        // blip here should leave the grid the lead is already using in place and
        // say so in a toast. `loadInitial` keeps its own fatal branch, because
        // before the first success there is no grid to leave in place.
        if (TRANSIENT_BOOKING_ERRORS.has(res.error)) {
          toast.error(
            res.message?.trim() || "Couldn't load times. Please try again."
          );
          return;
        }
        if (!fatal) return;
        setFatalError({ code: res.error, message: res.message ?? "" });
        setPhase("error");
        return;
      }

      setSlotsData(res);
      // The backend answers with the date it actually served, which is not
      // always the one asked for. Adopt it, or the heading names one day while
      // the grid underneath lists another day's times.
      if (res.date) setSelectedDate(res.date);
      setPhase("select");
    },
    [orgId, token]
  );

  /**
   * Whether a /slots response has ever seeded the page. Retry reads it: before
   * the first success the picker still holds the browser's guess and the link's
   * pinned zone has never been seen, so retrying with that guess would pin it
   * for the rest of the session.
   */
  const seededRef = useRef(false);

  // Initial load — omit durationMinutes so the backend defaults to allowedDurationMinutes[0]
  const loadInitial = useCallback(async (): Promise<void> => {
    // Read the clock once: the value is compared against below, and calling
    // todayInZone() again could land on the next day if the request spans
    // local midnight.
    const browserZone = getUserTimezone();
    const requestedDate = todayInZone(browserZone);
    // No `timezone` param on purpose (kris-backend#9713): the backend then
    // answers in the zone pinned on the link, which is how this page learns the
    // pinned zone without first painting a grid of wrong-zone times.
    const res = await getSlots(orgId, token, undefined, requestedDate);

    if (isErrorResponse(res)) {
      setFatalError({ code: res.error, message: res.message ?? "" });
      setPhase("error");
      return;
    }

    const defaultDuration =
      res.durationMinutes ?? res.allowedDurationMinutes[0] ?? 30;

    // Seeding rule: the zone pinned on the link if there is one, else the
    // lead's own browser. `""` — and `undefined`, from a backend older than
    // #9713 — is NOT a cue to fall back to aeTimezone: it is the state every
    // link created so far is in, and it means "follow the lead's browser".
    // Both zones are backend strings, so both go through safeZone: ZoneId.of
    // accepts values Intl throws on, and a throw here is a permanent spinner.
    const pickerZone = safeZone(res.defaultTimezone, browserZone);
    // The zone the slots in hand are actually formatted in, which is a
    // different question. With a pinned zone the backend already answered in
    // it and there is nothing to correct; with none it fell back to the AE's
    // own zone, which matches the lead's only by coincidence.
    const loadedZone = safeZone(res.timezone, pickerZone);
    // The day the lead will see under the heading, and the day the corrective
    // request below asks for — so it is chosen in the zone the lead is
    // actually looking at, not the one the response happens to be formatted
    // in. Reading it in `loadedZone` opens an unpinned link on the AE's date,
    // which around midnight is a day already past for the lead. Where the two
    // zones disagree about which dates exist, fetchSlots reconciles
    // `selectedDate` with the date the backend actually serves.
    const pickerToday = todayInZone(pickerZone);
    const firstDate =
      res.availableDates.find((d) => d >= pickerToday) ?? pickerToday;
    const loadedDate = res.date || requestedDate;

    seededRef.current = true;
    setSelectedDuration(defaultDuration);
    setSelectedDate(firstDate);
    setTimezone(pickerZone);
    setSlotsData(res);
    lastLoadedAtRef.current = Date.now();
    setPhase("select");

    // At most one corrective call, and only when what we already hold is not
    // what the lead should be looking at: the wrong zone, or the wrong day
    // within it. A link with a pinned zone lands here already correct and
    // issues no second request at all. Non-fatal: the grid on screen is usable
    // even if this never arrives, and a full-page error would be a downgrade.
    if (loadedZone !== pickerZone || firstDate !== loadedDate) {
      await fetchSlots(firstDate, defaultDuration, pickerZone, {
        fatal: false,
      });
    }
  }, [orgId, token, fetchSlots]);

  useEffect(() => {
    void loadInitial();
  }, [loadInitial]);

  /**
   * Refresh the grid when the lead returns to a page they left open.
   *
   * Deliberately narrow about when it fires:
   *
   * - only in `select`. `fetchSlots` clears the chosen slot, so running this
   *   while they are typing their details, or mid-booking, would pull the
   *   ground out from under them to fix a problem they do not have yet.
   * - only once the grid is older than `STALE_AFTER_MS`, so flicking to another
   *   tab and back does not reload it under the cursor.
   * - `fatal: false` — a blip while they were away must leave the page they
   *   already have, not replace it with an error panel.
   *
   * `focus` as well as `visibilitychange`: switching applications never hides
   * the document, and that is the shape of walking away from a laptop.
   */
  useEffect(() => {
    const refreshIfStale = (): void => {
      if (document.visibilityState !== "visible") return;
      if (phase !== "select") return;
      // `phase` alone is not enough. A lead the link already knows
      // (`hasContact`) never leaves `select` when they pick a time — they stay
      // here with the slot held and the Confirm block rendered — so refreshing
      // would clear `selectedSlot` and take the Confirm button away from
      // somebody one click from booking. That is the exact harm the phase check
      // was meant to prevent, and it did not cover them.
      if (selectedSlot) return;
      if (Date.now() - lastLoadedAtRef.current < STALE_AFTER_MS) return;
      // Clamped the way `handleRetry` clamps it. A page left open across
      // midnight in the lead's own zone still holds yesterday's date; asking for
      // it again lands them on an empty grid headed with yesterday, or on a
      // SLOT_EXPIRED toast about a slot they never picked.
      void fetchSlots(
        selectedDate || todayInZone(timezone),
        selectedDuration,
        timezone,
        { fatal: false }
      );
    };

    document.addEventListener("visibilitychange", refreshIfStale);
    window.addEventListener("focus", refreshIfStale);
    return (): void => {
      document.removeEventListener("visibilitychange", refreshIfStale);
      window.removeEventListener("focus", refreshIfStale);
    };
  }, [
    fetchSlots,
    phase,
    selectedSlot,
    selectedDate,
    selectedDuration,
    timezone,
  ]);

  const handleDateChange = useCallback(
    (date: string): void => {
      setSelectedDate(date);
      void fetchSlots(date, selectedDuration, timezone);
    },
    [fetchSlots, selectedDuration, timezone]
  );

  const handleDurationChange = useCallback(
    (duration: number): void => {
      setSelectedDuration(duration);
      if (selectedDate) void fetchSlots(selectedDate, duration, timezone);
    },
    [fetchSlots, selectedDate, timezone]
  );

  const handleTimezoneChange = useCallback(
    (tz: string): void => {
      setTimezone(tz);
      if (selectedDate) void fetchSlots(selectedDate, selectedDuration, tz);
    },
    [fetchSlots, selectedDate, selectedDuration]
  );

  const handleRetry = useCallback((): void => {
    setPhase("loading");
    // Nothing has seeded yet, so `timezone` is still the browser's guess and
    // the link's pinned zone has never been read. Re-run the same seeding path
    // the mount does rather than sending that guess: otherwise one transient
    // failure of the first call silently disables the pinned zone for the whole
    // session, with no way back short of a reload.
    if (!seededRef.current) {
      void loadInitial();
      return;
    }
    void fetchSlots(
      selectedDate || todayInZone(timezone),
      selectedDuration,
      timezone
    );
  }, [fetchSlots, loadInitial, selectedDate, selectedDuration, timezone]);

  const handleSlotSelect = (slot: SlotEntry): void => {
    setSelectedSlot(slot);
    if (slotsData && !slotsData.hasContact) {
      setPhase("details");
    }
  };

  // Imported on demand for the same reason the form is: the validator pulls in
  // libphonenumber's full metadata, and it cannot run before the lead has seen
  // the form. By then the form's chunk is loaded and this resolves from cache.
  const validateDetails = async (): Promise<boolean> => {
    if (slotsData?.hasContact) return true;

    const { validateLeadDetails } = await import("@/lib/validateLeadDetails");
    const errors = validateLeadDetails(leadDetails);
    if (Object.keys(errors).length > 0) {
      setLeadErrors(errors);
      return false;
    }
    return true;
  };

  const handleConfirm = async (): Promise<void> => {
    // A ref, not state: validateDetails awaits a dynamic import, so on the first
    // click there is a network round trip before setPhase("booking") disables
    // the button. Two clicks inside that window would both read the same stale
    // state and both POST — one books, the other comes back LINK_USED, and the
    // lead sees an error sitting on top of a booking that actually succeeded.
    if (submittingRef.current) return;
    submittingRef.current = true;
    try {
      await confirmBooking();
    } finally {
      submittingRef.current = false;
    }
  };

  const confirmBooking = async (): Promise<void> => {
    if (!selectedSlot || !slotsData) return;
    if (!(await validateDetails())) return;

    setPhase("booking");

    const body = {
      startTime: selectedSlot.startTime,
      durationMinutes: selectedDuration,
      recordingConsent: slotsData.recordingEnabled
        ? recordingConsent
        : undefined,
      notes: leadDetails.notes.trim() || undefined,
      leadName: !slotsData.hasContact ? leadDetails.name : undefined,
      leadEmail: !slotsData.hasContact ? leadDetails.email : undefined,
      leadPhone: !slotsData.hasContact ? leadDetails.phone : undefined,
      // Normalized to an absolute URL so what we store is clickable — a lead
      // typing "acmecorps.com" would otherwise be persisted schemeless.
      leadWebsite: !slotsData.hasContact
        ? normalizeWebsite(leadDetails.website)
        : undefined,
    };

    const res = await bookSlot(orgId, token, body);

    if (isErrorResponse(res)) {
      if (res.error === "SLOT_UNAVAILABLE") {
        toast.error("This slot was just taken — please pick another time.");
        setPhase("select");
        setSelectedSlot(null);
        void fetchSlots(selectedDate, selectedDuration, timezone);
        return;
      }
      if (res.error === "SLOT_EXPIRED") {
        toast.error("This slot is in the past — please pick another time.");
        setPhase("select");
        setSelectedSlot(null);
        void fetchSlots(selectedDate, selectedDuration, timezone);
        return;
      }
      // Their slot is still theirs and their answers are still valid — only the
      // attempt failed. Put them back on the step they were standing on, with
      // everything they typed intact, and let the backend's own sentence say
      // what to do. `leadDetails` is never cleared here: it is the whole point.
      if (TRANSIENT_BOOKING_ERRORS.has(res.error)) {
        toast.error(
          res.message?.trim() ||
            "Couldn't complete the booking. Please try again."
        );
        // hasContact leads never reach the details step — their Confirm button
        // lives on the slot grid — so that is the step to return them to.
        setPhase(slotsData.hasContact ? "select" : "details");
        return;
      }
      setFatalError({ code: res.error, message: res.message ?? "" });
      setPhase("error");
      return;
    }

    setBookResponse(res);
    setPhase("booked");
  };

  // ── Loading ────────────────────────────────────────────────────────
  if (phase === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-3">
          <div className="h-9 w-9 animate-spin rounded-full border-[3px] border-[#029391] border-t-transparent" />
          <p className="text-sm text-neutral-400">Loading availability…</p>
        </div>
      </div>
    );
  }

  // ── Error ──────────────────────────────────────────────────────────
  if (phase === "error") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white p-6">
        <div className="w-full max-w-md">
          <ErrorState
            errorCode={fatalError.code}
            serverMessage={fatalError.message}
            onRetry={handleRetry}
          />
        </div>
      </div>
    );
  }

  // ── Booked ─────────────────────────────────────────────────────────
  if (phase === "booked" && bookResponse && slotsData) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white p-6">
        <div className="w-full max-w-lg">
          <div className="overflow-hidden rounded-2xl border border-neutral-100 bg-white shadow-[0_8px_40px_rgba(0,0,0,0.10)]">
            <div className="flex items-center justify-between bg-[#E8F9F9] px-6 py-4">
              <div className="flex items-center gap-3">
                <AvatarOrInitials
                  name={slotsData.aeName}
                  avatarUrl={slotsData.aeAvatarUrl}
                />
                <div>
                  <p className="text-sm font-semibold text-neutral-900">
                    {slotsData.aeName}
                  </p>
                  {slotsData.meetingTitle && (
                    <p className="text-xs text-neutral-500">
                      {slotsData.meetingTitle}
                    </p>
                  )}
                </div>
              </div>
              <KrisBadge />
            </div>
            <div className="px-8 pb-10 pt-6">
              <BookingConfirmation
                bookResponse={bookResponse}
                timezone={timezone}
                durationMinutes={selectedDuration}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Details step (contact-less) ────────────────────────────────────
  // Also matches phase === "booking" so the user stays here during the API call
  // `slotsData` first: the branch to its right dereferences it. Unreachable
  // with a null today — phase cannot be "details" before a response lands — but
  // the guard was written after the thing it guards, which is only ever one
  // reordering away from being a crash.
  if (
    slotsData &&
    (phase === "details" || (phase === "booking" && !slotsData.hasContact))
  ) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white p-6">
        <div className="w-full max-w-lg">
          <div className="overflow-hidden rounded-2xl border border-neutral-100 bg-white shadow-[0_8px_40px_rgba(0,0,0,0.10)]">
            <div className="flex items-center justify-between bg-[#E8F9F9] px-6 py-4">
              <div className="flex items-center gap-3">
                <AvatarOrInitials
                  name={slotsData.aeName}
                  avatarUrl={slotsData.aeAvatarUrl}
                />
                <div>
                  <p className="text-sm font-semibold text-neutral-900">
                    {slotsData.aeName}
                  </p>
                  {slotsData.meetingTitle && (
                    <p className="text-xs text-neutral-500">
                      {slotsData.meetingTitle}
                    </p>
                  )}
                </div>
              </div>
              <KrisBadge />
            </div>
            <div className="p-8">
              <LeadDetailsForm
                value={leadDetails}
                onChange={(d) => {
                  setLeadDetails(d);
                  setLeadErrors({});
                }}
                errors={leadErrors}
              />
              {slotsData.recordingEnabled && (
                <div className="mt-5 border-t border-neutral-100 pt-5">
                  <RecordingConsentCheckbox
                    checked={recordingConsent}
                    onChange={setRecordingConsent}
                  />
                </div>
              )}
              <div className="mt-8 flex items-center justify-end gap-3">
                <button
                  onClick={() => setPhase("select")}
                  className="rounded-full border border-neutral-200 px-6 py-2.5 text-sm font-medium text-neutral-600 transition-colors hover:bg-neutral-50"
                >
                  Back
                </button>
                <button
                  onClick={() => void handleConfirm()}
                  disabled={
                    phase === "booking" ||
                    (slotsData.recordingEnabled && !recordingConsent)
                  }
                  className="rounded-full bg-[#029391] px-8 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-[#027a78] hover:shadow-md disabled:opacity-60"
                >
                  {phase === "booking" ? (
                    <span className="flex items-center gap-2">
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      Booking…
                    </span>
                  ) : (
                    "Confirm"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!slotsData) return null;

  // ── Select step ────────────────────────────────────────────────────
  const selectedDateFormatted = selectedDate
    ? new Date(selectedDate + "T12:00:00").toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
      })
    : null;

  return (
    <div className="flex min-h-screen items-start justify-center bg-white px-4 py-10">
      <div className="w-full max-w-[960px]">
        {/* Card */}
        <div className="overflow-hidden rounded-3xl border border-neutral-100 bg-white shadow-[0_8px_48px_rgba(0,0,0,0.10)]">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-[#d0f0ef] bg-[#E8F9F9] px-8 py-5">
            <div className="flex items-center gap-4">
              <AvatarOrInitials
                name={slotsData.aeName}
                avatarUrl={slotsData.aeAvatarUrl}
                size="lg"
              />
              <div>
                <p className="text-base font-bold text-neutral-900">
                  {slotsData.aeName}
                </p>
                {slotsData.meetingTitle && (
                  <p className="mt-0.5 text-sm text-neutral-500">
                    {slotsData.meetingTitle}
                  </p>
                )}
              </div>
            </div>
            <KrisBadge />
          </div>

          {/* Two-column body */}
          <div className="grid grid-cols-1 md:grid-cols-[420px_1fr]">
            {/* LEFT — calendar + controls */}
            <div className="border-r border-neutral-100 bg-[#FAFFFE] p-8">
              <BookingCalendar
                availableDates={slotsData.availableDates}
                selectedDate={selectedDate}
                onSelectDate={handleDateChange}
              />

              {slotsData.allowedDurationMinutes.length > 1 && (
                <div className="mt-7">
                  <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-neutral-400">
                    Duration
                  </p>
                  <DurationPills
                    options={slotsData.allowedDurationMinutes}
                    selected={selectedDuration}
                    onChange={handleDurationChange}
                  />
                </div>
              )}

              <div className="mt-6">
                <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-neutral-400">
                  Time Zone
                </p>
                <TimezoneSelector
                  value={timezone}
                  onChange={handleTimezoneChange}
                />
              </div>
            </div>

            {/* RIGHT — agenda + slot list */}
            <div className="flex flex-col p-8">
              <MeetingAgenda description={slotsData.description} />

              <div className="mb-5">
                <p className="text-xs font-semibold uppercase tracking-widest text-neutral-400">
                  Select a time
                </p>
                {selectedDateFormatted && (
                  <h2 className="mt-1 text-xl font-bold text-neutral-900">
                    {selectedDateFormatted}
                  </h2>
                )}
              </div>

              <div className="min-h-0 flex-1">
                <TimeSlotList
                  slots={slotsData.slots}
                  selectedSlot={selectedSlot}
                  onSelectSlot={handleSlotSelect}
                  timezone={timezone}
                  loading={slotsLoading}
                />
              </div>

              {selectedSlot && slotsData.hasContact && (
                <div className="mt-6 border-t border-neutral-100 pt-6">
                  {slotsData.recordingEnabled && (
                    <div className="mb-5">
                      <RecordingConsentCheckbox
                        checked={recordingConsent}
                        onChange={setRecordingConsent}
                      />
                    </div>
                  )}
                  <button
                    onClick={() => void handleConfirm()}
                    disabled={
                      phase === "booking" ||
                      (slotsData.recordingEnabled && !recordingConsent)
                    }
                    className={cn(
                      "w-full rounded-2xl py-4 text-sm font-semibold transition-all",
                      phase !== "booking" &&
                        !(slotsData.recordingEnabled && !recordingConsent)
                        ? "bg-[#029391] text-white shadow-md hover:bg-[#027a78] hover:shadow-lg"
                        : "cursor-not-allowed bg-neutral-100 text-neutral-400"
                    )}
                  >
                    {phase === "booking" ? (
                      <span className="flex items-center justify-center gap-2">
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        Booking…
                      </span>
                    ) : (
                      "Confirm Booking"
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
